import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { terraProxyApiClient } from '../clients/testerClient';
import type { FindingTimelineDto } from '@terra/shared-contracts-findings';

export const FINDING_TIMELINE_QUERY_KEY = 'query.finding.timeline';

export const useFindingTimeline = ({
  id,
  enabled = true,
}: {
  id: string | null;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey: [FINDING_TIMELINE_QUERY_KEY, id],
    queryFn: async () => {
      if (!id) {
        return null;
      }

      const response = await terraProxyApiClient.getFindingTimeline(id);

      if (response?.status === 200) {
        return response.body as FindingTimelineDto;
      }

      return null;
    },
    enabled: !!id && enabled,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const invalidate = useCallback(async () => {
    if (!id) return;
    await queryClient.invalidateQueries({
      queryKey: [FINDING_TIMELINE_QUERY_KEY, id],
      exact: false,
    });
  }, [queryClient, id]);

  return {
    timeline: result.data ?? null,
    isLoading: result.isLoading,
    error: result.error,
    invalidate,
  };
};
